import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { postData } from '../services/api.service';

const AuthContext = createContext();

// Definición de módulos por rol
const ROLE_PERMISSIONS = {
  1: { // Administrador - ACCESO TOTAL
    name: 'Administrador',
    modules: ['dashboard', 'pedidos', 'reportes', 'stock', 'taller', 'finanzas', 'comisiones', 'administracion', 'configuracion'],
    canViewAllOrders: true,
    canEditConfig: true,
    canEditStock: true // Admin puede editar stock
  },
  2: { // Vendedor - SOLO PEDIDOS + STOCK (lectura)
    name: 'Vendedor',
    modules: ['pedidos', 'stock'],
    canViewAllOrders: false, // Solo sus propios pedidos
    canEditConfig: false,
    canEditStock: false // Vendedor solo lectura en stock
  },
  3: { // Finanzas
    name: 'Finanzas',
    modules: ['dashboard', 'pedidos', 'reportes', 'stock', 'taller', 'finanzas', 'comisiones', 'configuracion'],
    canViewAllOrders: true,
    canEditConfig: true, // Finanzas puede configurar
    canEditStock: true // Finanzas puede editar stock
  },
  4: { // Taller
    name: 'Taller',
    modules: ['dashboard', 'taller'],
    canViewAllOrders: false,
    canEditConfig: false,
    canEditStock: false
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Cargar usuario del localStorage al iniciar
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await postData('/api/auth/login', credentials);
      
      // El backend devuelve: { success, msg, data: { user: {...}, token: '...' } }
      // Axios lo envuelve: response.data = { success, msg, data: { user: {...}, token: '...' } }
      if (response.data && response.data.data && response.data.data.user) {
        const userData = {
          ...response.data.data.user,
          token: response.data.data.token,
          permissions: ROLE_PERMISSIONS[response.data.data.user.role_id] || ROLE_PERMISSIONS[2]
        };
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', response.data.data.token);
        
        // Redirigir según el rol
        redirectByRole(userData.role_id);
        
        return { success: true, user: userData };
      }
      
      return { success: false, message: 'Credenciales inválidas' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.msg || 'Error al iniciar sesión' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const redirectByRole = (roleId) => {
    switch(roleId) {
      case 2: // Vendedor - ir directo a Pedidos
        navigate('/dashboard/pedidos');
        break;
      default:
        // Todos los demás al dashboard principal
        navigate('/dashboard');
    }
  };

  const hasModule = (module) => {
    if (!user || !user.permissions) return false;
    return user.permissions.modules.includes(module);
  };

  const canViewAllOrders = () => {
    if (!user || !user.permissions) return false;
    return user.permissions.canViewAllOrders;
  };

  const canEditConfig = () => {
    if (!user || !user.permissions) return false;
    return user.permissions.canEditConfig;
  };

  const canEditStock = () => {
    if (!user || !user.permissions) return false;
    return user.permissions.canEditStock;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasModule,
    canViewAllOrders,
    canEditConfig,
    canEditStock,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
